import React, { Ref, useId } from 'react';
import DayPicker, { Modifier, RangeModifier } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { Trans, useLingui } from '@lingui/react/macro';
import moment from 'moment';

import SwitchWithText from '@rover/components/src/buttons/OnOffSwitch/SwitchWithText';
import {
  DatePickerCaption,
  DatePickerNavbar,
  DatePickerStylingWrapper,
} from '@rover/components/src/datetime/DatePicker';
import DayOfWeekPicker from '@rover/components/src/datetime/DayOfWeekPicker';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import {
  RECURRING_SWITCH_INFO_CONTENT,
  RECURRING_SWITCH_INFO_TITLE,
  RECURRING_SWITCH_TEXT_LONG,
} from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/constants';
import { formatDateRange } from '@rover/react-lib/src/pages/search/SearchPage/components/StickyFiltersBar/utils';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';

import { RevampDatesSelection } from './useRevampDatesSelection';

type Props = {
  selection: RevampDatesSelection;
  locale: Locale;
  monthsShown: number;
  isMobileBrowser?: boolean;
  // Daytime services are booked day by day, so their days need not be contiguous.
  allowsLooseDates?: boolean;
  // Services billed weekly can repeat, so they offer the one-time / repeat switch.
  allowsRecurring?: boolean;
  onRecurringInfoPress?: () => void;
  recurringInfoTriggerRef?: Ref<HTMLElement>;
  testID: string;
};

const RevampDatesPicker = ({
  selection,
  locale,
  monthsShown,
  isMobileBrowser,
  allowsLooseDates,
  allowsRecurring,
  onRecurringInfoPress,
  recurringInfoTriggerRef,
  testID,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const theme = useTheme();
  const weekDaysLabelId = useId();
  const {
    range,
    looseDates,
    isRepeating,
    setRepeats,
    selectedDayOfWeekOptions,
    handleDayClick,
    handleWeekDaysChange,
  } = selection;

  const today = moment().startOf('day').toDate();
  const maxDate = getMaxRequestDate();

  const selectedRange: RangeModifier | undefined =
    range.startDate && range.endDate ? { from: range.startDate, to: range.endDate } : undefined;

  // The days in between are styled apart from the endpoints, so they exclude them.
  const daysBetween: RangeModifier | undefined =
    range.startDate && range.endDate
      ? {
          from: moment(range.startDate).add(1, 'day').toDate(),
          to: moment(range.endDate).subtract(1, 'day').toDate(),
        }
      : undefined;

  return (
    <Flex flexDirection="column" data-testid={testID}>
      {allowsRecurring && (
        <Box mb="2x" data-testid={`${testID}-frequency`}>
          <SwitchWithText
            textContent={i18n._(RECURRING_SWITCH_TEXT_LONG)}
            extraInfoContent={i18n._(RECURRING_SWITCH_INFO_CONTENT)}
            extraInfoTitle={i18n._(RECURRING_SWITCH_INFO_TITLE)}
            onExtraInfoPress={onRecurringInfoPress}
            extraInfoTriggerRef={recurringInfoTriggerRef}
            on={isRepeating}
            onChange={setRepeats}
            bigLabel={!isMobileBrowser}
          />
        </Box>
      )}

      {isRepeating && (
        <Box mb="2x">
          <Text
            size="200"
            fontWeight="semibold"
            mb="1x"
            webProps={{ as: 'label', id: weekDaysLabelId }}
          >
            <Trans>What days?</Trans>
          </Text>
          <DayOfWeekPicker
            aria-labelledby={weekDaysLabelId}
            locale={locale}
            selectedDays={selectedDayOfWeekOptions}
            onChange={handleWeekDaysChange}
            bigLabel={!isMobileBrowser}
          />
        </Box>
      )}

      {isRepeating && (
        <Box mb="2x">
          <Text size="200" fontWeight="semibold" mb="1x">
            <Trans>When would you like to start?</Trans>
          </Text>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="2x"
            px="4x"
            py="3x"
            borderRadius="8px"
            border={`1px solid ${theme.colors.separator.primary}`}
            data-testid={`${testID}-start-date`}
          >
            <Text size="200" textColor={range.startDate ? 'primary' : 'secondary'}>
              {range.startDate
                ? formatDateRange(range.startDate, undefined, locale)
                : t`Select your start date`}
            </Text>
            <Calendar
              width="20"
              height="20"
              fill={theme.colors.text.secondary.toString()}
              aria-hidden="true"
            />
          </Flex>
        </Box>
      )}

      <Box
        data-testid={`${testID}-calendar`}
        maxHeight={isMobileBrowser ? '55vh' : undefined}
        sx={{ overflowY: isMobileBrowser ? 'auto' : undefined }}
      >
        <DatePickerStylingWrapper
          newCalendarInteraction
          hideRangeConnector
          isMobileBrowser={isMobileBrowser}
        >
          <DayPicker
            numberOfMonths={monthsShown}
            initialMonth={
              (allowsLooseDates && !isRepeating ? looseDates[0] : range.startDate) ?? today
            }
            fromMonth={today}
            toMonth={maxDate}
            locale={locale}
            localeUtils={MomentLocaleUtils}
            navbarElement={DatePickerNavbar}
            captionElement={DatePickerCaption}
            selectedDays={(() => {
              if (isRepeating) return [range.startDate].filter(Boolean) as Modifier[];
              if (allowsLooseDates) return looseDates;
              return [range.startDate, selectedRange].filter(Boolean) as Modifier[];
            })()}
            disabledDays={[{ before: today, after: maxDate }]}
            // A repeating start date and the loose days are standalone selections, so
            // they take none of the modifiers that draw the bar connecting a range.
            modifiers={
              isRepeating || allowsLooseDates
                ? {}
                : {
                    start: range.startDate ?? [],
                    end: range.endDate ?? [],
                    between: daysBetween ?? [],
                  }
            }
            onDayClick={handleDayClick}
          />
        </DatePickerStylingWrapper>
      </Box>
    </Flex>
  );
};

export default RevampDatesPicker;
